import { useEffect, useState } from "react";
import { Plus, Copy, Trash2, Ticket, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";

export default function SuperVouchers() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ duration: "1y", count: 1, max_uses: 1 });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/vouchers")
      .then((r) => setVouchers(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/vouchers", form);
      toast.success(`${data.codes.length} voucher code(s) generated`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (v) => {
    try {
      await api.put(`/admin/vouchers/${v.id}/toggle`);
      toast.success(v.active ? "Voucher deactivated" : "Voucher reactivated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete voucher ${v.code}?`)) return;
    try {
      await api.delete(`/admin/vouchers/${v.id}`);
      toast.success("Voucher deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="super-vouchers-page">
      <span className="nb-label">Vouchers</span>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Free access codes</h1>
      <p className="text-zinc-500 mt-2 max-w-xl">Generate voucher codes to give parents free access — one school year or the full six years. Parents redeem them on their paywall screen.</p>

      <div className="mt-8 nb-card p-6">
        <form onSubmit={generate} className="flex flex-wrap items-end gap-4">
          <div>
            <label className="nb-label" htmlFor="voucher-duration">Duration</label>
            <select id="voucher-duration" className="nb-input mt-2 w-44" data-testid="voucher-duration-select"
              value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })}>
              <option value="1y">1 year free</option>
              <option value="6y">Full 6 years free</option>
            </select>
          </div>
          <div>
            <label className="nb-label" htmlFor="voucher-count">How many codes</label>
            <input id="voucher-count" type="number" min="1" max="50" className="nb-input mt-2 w-28" data-testid="voucher-count-input"
              value={form.count} onChange={(e) => setForm({ ...form, count: parseInt(e.target.value) || 1 })} />
          </div>
          <div>
            <label className="nb-label" htmlFor="voucher-uses">Uses per code</label>
            <input id="voucher-uses" type="number" min="1" max="100" className="nb-input mt-2 w-28" data-testid="voucher-uses-input"
              value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value) || 1 })} />
          </div>
          <button type="submit" disabled={busy} data-testid="voucher-generate-btn" className="nb-btn bg-[#A7E4D3] px-5 py-2.5">
            <Plus className="w-4 h-4" /> {busy ? "Generating…" : "Generate"}
          </button>
        </form>
      </div>

      <div className="mt-6 nb-card overflow-x-auto">
        <table className="w-full border-collapse min-w-[720px]" data-testid="vouchers-table">
          <thead>
            <tr className="bg-zinc-900 text-white text-left">
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Code</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Duration</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Uses</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Status</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Created</th>
              <th className="p-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {vouchers.map((v) => {
              const exhausted = v.used_count >= v.max_uses;
              return (
                <tr key={v.id} className="border-t-2 border-zinc-900" data-testid={`voucher-row-${v.id}`}>
                  <td className="p-3">
                    <span className="font-mono font-bold" data-testid={`voucher-code-${v.id}`}>{v.code}</span>
                    <button onClick={async () => { const ok = await copyToClipboard(v.code); ok ? toast.success("Copied!") : toast.error("Couldn't copy — select the code manually"); }} data-testid={`voucher-copy-${v.id}`}
                      className="ml-2 inline-flex p-1 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100 align-middle">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-lg border-2 border-zinc-900 text-xs font-bold ${v.duration === "6y" ? "bg-[#D8B4FE]" : "bg-[#7DD3FC]"}`}>
                      {v.duration === "6y" ? "6 years" : "1 year"}
                    </span>
                  </td>
                  <td className="p-3 font-bold">{v.used_count} / {v.max_uses}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-lg border-2 border-zinc-900 text-xs font-bold ${!v.active ? "bg-zinc-200 text-zinc-500" : exhausted ? "bg-[#FDE68A]" : "bg-[#A7E4D3]"}`}
                      data-testid={`voucher-status-${v.id}`}>
                      {!v.active ? "inactive" : exhausted ? "used up" : "active"}
                    </span>
                  </td>
                  <td className="p-3 text-sm text-zinc-500">{new Date(v.created_at).toLocaleDateString("en-GB")}</td>
                  <td className="p-3">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => toggle(v)} data-testid={`voucher-toggle-${v.id}`} title={v.active ? "Deactivate" : "Reactivate"}
                        className={`p-2 rounded-lg border-2 border-zinc-900 transition-colors duration-150 ${v.active ? "bg-[#FDE68A] hover:brightness-105" : "bg-white hover:bg-zinc-100"}`}>
                        {v.active ? <Ban className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                      </button>
                      <button onClick={() => remove(v)} data-testid={`voucher-delete-${v.id}`}
                        className="p-2 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150">
                        <Trash2 className="w-4 h-4 text-red-700" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {loading && (
              <tr><td colSpan={6} className="p-10 text-center border-t-2 border-zinc-900" data-testid="vouchers-loading">
                <p className="text-zinc-500 text-sm font-bold animate-pulse">Loading vouchers…</p>
              </td></tr>
            )}
            {!loading && vouchers.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center border-t-2 border-zinc-900" data-testid="vouchers-empty">
                <Ticket className="w-8 h-8 mx-auto text-zinc-300 mb-3" />
                <p className="text-zinc-500 text-sm">No vouchers yet. Generate your first codes above.</p>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
